import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Paper } from "@mui/material";
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
  RowStyle,
  RowDoubleClickedEvent,
  DefaultMenuItem,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { useCasterProcessesContext } from "../context/CasterProcessesContext";
import { useCasterOptionFunctions } from "../hooks/useCasterOptionFunctions";
import { ProcessByShift } from "../models/ProcessByShift";
import {
  deleteProcess,
  unDeleteProcess,
  unLinkCasterProcess,
} from "../repositories/CasterRepository";
import { LinkCasterProcess } from "./LinkCasterProcess";
import { UpsertCasterProcess } from "./UpsertCasterProcess";
import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { useTableData } from "./hooks";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { upsertProcessData } from "../repositories/CasterRepository";

type Props = {
  loadingGrid: boolean;
  rows: ProcessByShift[];
  refreshData: () => Promise<void>;
};

export const CasterProcessesTable = ({
  loadingGrid,
  rows,
  refreshData,
}: Props) => {
  const gridRef = useRef<AgGridReact>(null);
  const [positionToOpenModal, setPositionToOpenModal] = useState<{
    top: number;
    left: number;
  }>();

  const {
    state: { selectedProcess, errorMessage, processSelected },
    actions: {
      setSelectedProcess,
      setIsLoadingBackground,
      setNotificationMessage,
      setErrorMessage,
    },
  } = useCasterProcessesContext();

  const handleShowCommentModal = (
    show: boolean,
    data: ProcessByShift,
    position?: { top: number; left: number },
  ) => {
    if (data) {
      setSelectedProcess(data);
    }
    setPositionToOpenModal(position);
    setShowCommentModal(show);
  };

  const {
    createData,
    editData,
    linkData,
    unlinkData,
    getMenuOptions,
    toggleStatusData,
    goToDataEntry,
    showActiveModal,
    showDeleteModal,
    showEditModal,
    showLinkModal,
    showUnlinkModal,
    showCommentModal,
    messageActiveModal,
    messageDeleteModal,
    messageUnlinkModal,
    setShowActiveModal,
    setShowDeleteModal,
    setShowEditModal,
    setShowLinkModal,
    setShowUnlinkModal,
    setShowCommentModal,
    setMessageActiveModal,
    setMessageDeleteModal,
  } = useCasterOptionFunctions();

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const { columnDefs } = useTableData({
    linkData: linkData,
    unlinkData: unlinkData,
    toggleStatusData: toggleStatusData,
    setShowCommentModal: handleShowCommentModal,
    showContextMenu: showContextMenu,
  });

  const onUnlinkSubmit = async (value?: string) => {
    setShowUnlinkModal(false);
    if (value !== "OK") return;
    await unLinkCasterProcess(selectedProcess!.ProcessId);
    await refreshData();
  };

  const deleteCasterProcess = async (value?: string) => {
    setShowDeleteModal(false);
    setMessageDeleteModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);
    const resp = await deleteProcess(selectedProcess.ProcessId);
    if (resp.ok) {
      setIsLoadingBackground(false);
      setNotificationMessage("Process deleted successfully");
      setSelectedProcess(null);
      await refreshData();
    } else {
      setIsLoadingBackground(false);
      setErrorMessage(resp.message);
    }
  };

  const activeCasterProcess = async (value?: string) => {
    setShowActiveModal(false);
    setMessageActiveModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);
    const resp = await unDeleteProcess(selectedProcess.ProcessId);
    if (resp.ok) {
      setIsLoadingBackground(false);
      setNotificationMessage("Process activated successfully");
      setSelectedProcess(null);
      await refreshData();
    } else {
      setIsLoadingBackground(false);
      setErrorMessage(resp.message);
    }
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams<ProcessByShift>,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;

    if (data) {
      params.api.deselectAll();
      params.node?.setSelected(true);
      setSelectedProcess(data);
    }

    showContextMenu(
      event as unknown as React.MouseEvent,
      data,
      "casterProcessesContext",
    );

    return [];
  };

  const getRowStyle = (
    params: RowClassParams<ProcessByShift>,
  ): RowStyle | undefined => {
    if (params.data?.IsActive) {
      return { background: "#E8EBEB" };
    }
    if (params.data?.ProcessId === processSelected) {
      params.node.setSelected(true);
    }
    return undefined;
  };

  useEffect(() => {
    registerConfig({
      id: "casterProcessesContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <Grid onContextMenu={(e) => e.preventDefault()}>
      <Grid>
        <Grid container>
          <Grid
            component={Paper}
            elevation={3}
            item
            md={12}
            xs={12}
            style={{
              padding: 10,
              height: "calc(100vh - 300px)",
              minHeight: "calc(100vh - 300px)",
            }}
          >
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageDeleteModal}
              open={showDeleteModal}
              onClose={deleteCasterProcess}
            />
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageActiveModal}
              open={showActiveModal}
              onClose={activeCasterProcess}
            />
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageUnlinkModal}
              open={showUnlinkModal}
              onClose={onUnlinkSubmit}
            />
            <UpsertCasterProcess
              show={showEditModal}
              onHide={() => setShowEditModal(false)}
              refreshData={refreshData}
            />
            <LinkCasterProcess
              show={showLinkModal}
              onHide={() => setShowLinkModal(false)}
              refreshData={refreshData}
            />
            <CommentModal
              show={showCommentModal}
              onHide={() => {
                setShowCommentModal(false);
              }}
              refreshData={refreshData}
              position={positionToOpenModal}
              selectedElement={
                selectedProcess
                  ? { ...selectedProcess, ElementId: selectedProcess.ProcessId }
                  : null
              }
              setSelectedElement={setSelectedProcess}
              upsertFunction={upsertProcessData}
            />
            <AgGridReact<ProcessByShift>
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={(
                event: RowDoubleClickedEvent<ProcessByShift>,
              ) => {
                goToDataEntry(event.data?.ProcessId ?? null);
              }}
              onRowClicked={(event) => setSelectedProcess(event.data)}
              loading={loadingGrid}
              {...defaultGridOptions}
              suppressContextMenu={false}
            />
            <Grid container justifyContent="flex-end">
              <Grid
                item
                md={2}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  style={{ margin: "1.5rem 0px 0px" }}
                  onClick={() => {
                    createData();
                  }}
                >
                  NEW PROCESS
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
    </Grid>
  );
};
